import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent } from '../ui/card';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom pulsing marker icon with CSS animation and page-based colors
const createPulsingIcon = (deviceType, currentPage) => {
  // Determine color based on the page visitor is viewing
  let color = '#3b82f6'; // Default blue
  let blinkAnimation = 'pulse-marker 2s ease-out infinite';
  
  // Check the current page URL to assign specific colors
  // Convert to lowercase for case-insensitive matching
  const pageUrl = currentPage ? currentPage.toLowerCase() : '';
  
  if (pageUrl) {
    if (pageUrl.includes('/employee-portal/login') || pageUrl.includes('employee-portal')) {
      color = '#eab308'; // Yellow for employee portal
      blinkAnimation = 'pulse-blink 1s ease-in-out infinite'; // Faster blink for employee
    } else if (pageUrl.includes('/customer-admin/dashboard') || pageUrl.includes('customer-admin')) {
      color = '#ef4444'; // Red for customer admin dashboard
    } else if (pageUrl.includes('/make-payment') || pageUrl.includes('payment')) {
      color = '#f97316'; // Orange for payment page
      blinkAnimation = 'pulse-blink 1s ease-in-out infinite'; // Blinking for payment
    } else if (pageUrl.includes('/place-order-form') || pageUrl.includes('order')) {
      color = '#92400e'; // Brown for order form page
      blinkAnimation = 'pulse-blink 1s ease-in-out infinite'; // Blinking for order
    } else {
      // Default colors based on device type for regular visitors
      color = deviceType === 'Mobile' ? '#8b5cf6' : deviceType === 'Tablet' ? '#10b981' : '#3b82f6';
    }
  } else {
    // No page info, use device type colors
    color = deviceType === 'Mobile' ? '#8b5cf6' : deviceType === 'Tablet' ? '#10b981' : '#3b82f6';
  }
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative; width: 30px; height: 30px;">
        <div style="
          width: 30px;
          height: 30px;
          background: ${color};
          border: 4px solid white;
          border-radius: 50%;
          box-shadow: 0 0 15px rgba(0,0,0,0.5);
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1000;
        "></div>
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 30px;
          height: 30px;
          background: ${color};
          border-radius: 50%;
          opacity: 0.7;
          animation: ${blinkAnimation};
          z-index: 999;
        "></div>
      </div>
      <style>
        @keyframes pulse-marker {
          0% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(2);
            opacity: 0.4;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }
        @keyframes pulse-blink {
          0% {
            transform: scale(1);
            opacity: 0.9;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
          100% {
            transform: scale(1);
            opacity: 0.9;
          }
        }
      </style>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

const LiveVisitorsMap = ({ visitors }) => {
  // Filter visitors with valid coordinates
  const validVisitors = visitors.filter(
    v => v.latitude && v.longitude && v.latitude !== 0 && v.longitude !== 0
  );

  // Calculate center (default to India if no visitors)
  const center = validVisitors.length > 0
    ? [validVisitors[0].latitude, validVisitors[0].longitude]
    : [20.5937, 78.9629]; // India center

  return (
    <Card className="h-[500px] shadow-lg">
      <CardContent className="p-3 h-full">
        <div className="mb-2">
          <h3 className="text-sm font-bold text-gray-900 mb-1.5">Visitor Locations (Live)</h3>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
              <span className="text-gray-600 text-xs">Employee</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <span className="text-gray-600 text-xs">Admin</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
              <span className="text-gray-600 text-xs">Payment</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-800"></div>
              <span className="text-gray-600 text-xs">Order</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
              <span className="text-gray-600 text-xs">Regular</span>
            </div>
          </div>
        </div>
        <div className="h-[450px] rounded-lg overflow-hidden border border-emerald-200">
          {typeof window !== 'undefined' && (
            <MapContainer
              center={center}
              zoom={validVisitors.length > 0 ? 5 : 5}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {validVisitors.map((visitor, index) => (
                <Marker
                  key={visitor.sessionId || index}
                  position={[visitor.latitude, visitor.longitude]}
                  icon={createPulsingIcon(visitor.deviceType, visitor.currentPage)}
                >
                  <Popup>
                    <div className="p-2">
                      <p className="font-bold text-emerald-700">{visitor.city}, {visitor.state}</p>
                      <p className="text-xs text-gray-500">{visitor.country}</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm font-semibold">{visitor.deviceType} - {visitor.browser}</p>
                        <p className="text-xs text-gray-600">OS: {visitor.os}</p>
                        <p className="text-xs text-gray-600">IP: {visitor.ipAddress}</p>
                        <p className="text-xs text-gray-600">ISP: {visitor.isp}</p>
                        <p className="text-xs text-gray-500 mt-2 border-t pt-1">
                          Page: {visitor.currentPage?.substring(0, 40)}...
                        </p>
                        <p className="text-xs text-emerald-600 font-semibold">
                          Duration: {Math.floor(visitor.duration / 60)}m {visitor.duration % 60}s
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {validVisitors.length} visitor{validVisitors.length !== 1 ? 's' : ''} on map
        </p>
      </CardContent>
    </Card>
  );
};

export default LiveVisitorsMap;